import { Component, ElementRef, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { EngagementService } from 'src/app/care/engagement/engagement.service';
import { ChooseTemplateFlowService } from 'src/app/shared/components/choose-template-flow/shared/choose-template-flow.service';
import { EngagementTemplateFlowStepEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-template-flow-step.enum';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { AttachmentMediaTypeEnum } from 'src/app/shared/models/misc.model';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-preview-and-send',
  templateUrl: './preview-and-send.component.html',
  styleUrls: [
    '../../../../task-process/answer-editor/answer-editor.component.scss',
    './preview-and-send.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class PreviewAndSendComponent implements OnDestroy {
  @Input() templateDisplayedBS: BehaviorSubject<FormGroup> = new BehaviorSubject(null);

  subscriptions: Subscription = new Subscription();

  selectedTemplate: any = this.chooseTemplateFlowService.selectedTemplate;
  AttachmentMediaTypeEnum = AttachmentMediaTypeEnum;
  uploading: boolean;
  uploadProgress$: BehaviorSubject<number> = new BehaviorSubject(0);
  supportedMediaTypes: string;
  uploadMediaTypeLabel: string;
  editMediaTypeLabel: string;
  deleteMediaTypeLabel: string;
  isWebMode = environment.bundledWithAngularJSApp;

  EngagementTemplateFlowStepEnum = EngagementTemplateFlowStepEnum;
  SiteTypeEnum = SiteTypeEnum;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  get taskProcessContext(): TaskProcessContext {
    return this.chooseTemplateFlowService.taskProcessContext;
  }
  constructor(
    public engagementService: EngagementService,
    public chooseTemplateFlowService: ChooseTemplateFlowService
  ) {
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        this.checkWhatsappValidity();
        break;
      case SiteTypeEnum.Kakaotalk:
        this.checkKakaotalkValidity();
        break;
      default:
        this.checkWhatsappValidity();
    }
  }

  changeTemplate() {
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_TEMPLATE_MESSAGE;
  }

  back() {
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.COMPOSE_MESSAGE;
  }

  checkWhatsappValidity() {
    if (!this.selectedTemplate) {
      return false;
    }
    this.selectedTemplate.preview = this.getPreview(this.selectedTemplate, false);
    let hasHeaderValid = true;
    if (this.selectedTemplate.header) {
      if (this.selectedTemplate.header.type === 'text') {
        hasHeaderValid = !!this.selectedTemplate.header.text;
      } else {
        const { media_url } = this.selectedTemplate.header;
        hasHeaderValid = !!media_url;
      }
    }
    const form: FormGroup = new FormGroup({
      header: new FormGroup({
        text: new FormControl(),
        type: new FormControl(),
        media_type: new FormControl(),
        media_url: new FormControl(),
        filename: new FormControl(),
      }),
      body: new FormControl({}),
      buttons: new FormArray([]),
      footer: new FormControl(),
    });

    const { header, preview, footer, buttons } = this.selectedTemplate;

    (buttons || []).forEach(() =>
      (form.get('buttons') as FormArray).push(
        new FormGroup({
          text: new FormControl(),
          type: new FormControl(),
          url: new FormControl(),
          example: new FormArray([]),
        })
      )
    );

    form.patchValue({ header, body: preview, footer, buttons });
    this.chooseTemplateFlowService.templateDisplayedBS.next(form);
    this.chooseTemplateFlowService.isValid =
      hasHeaderValid && this.selectedTemplate.parameters.every(parameter => !!parameter.value);
  }

  checkKakaotalkValidity() {
    if (!this.selectedTemplate) {
      return false;
    }
    this.selectedTemplate.preview = this.getPreview(this.selectedTemplate, false);
    let hasHeaderValid = true;
    if (this.selectedTemplate.header?.media_type) {
      const { media_id } = this.selectedTemplate.header;
      hasHeaderValid = !!media_id;
    }
    const form: FormGroup = new FormGroup({
      header: new FormGroup({
        type: new FormControl(),
        media_type: new FormControl(),
        media_url: new FormControl(),
        filename: new FormControl(),
      }),
      body: new FormControl({}),
      buttons: new FormArray([]),
    });

    const { header, preview, buttons } = this.selectedTemplate;

    (buttons || []).forEach(() =>
      (form.get('buttons') as FormArray).push(
        new FormGroup({
          text: new FormControl(),
          type: new FormControl(),
          url: new FormControl(),
          example: new FormArray([]),
        })
      )
    );

    form.patchValue({ header, body: preview, buttons });
    this.chooseTemplateFlowService.templateDisplayedBS.next(form);
    this.chooseTemplateFlowService.isValid =
      hasHeaderValid && this.selectedTemplate.parameters.every(parameter => !!parameter.value);
  }

  private getPreview(template: any, forcePlaceholder: boolean): string {
    let result = template.text || template.body;
    let index = 1;
    for (const parameter of template.parameters) {
      const placeholder = `<${parameter.placeholder?.label || parameter.placeholder}>`;
      const replacement = forcePlaceholder ? placeholder : parameter.value || placeholder;
      result = result.replace(`{{${index++}}}`, replacement);
    }
    return result;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
